/** Checkbox multi-select over a resource's tag sources — which entities' tags
 *  count as "present in the context" for a tag condition. Options only ever come
 *  from the resource's registry endpoint (never hardcode source names).
 *
 * Part of the TagConditionInput vocabulary: shared by the notification
 * feature-flag dialogs and the delivery-blacklist rule editor. */
import { Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';

/** One registry entry: `{codename, name}` (a `description` may ride along). */
export interface TagSourceOption {
  codename: string;
  name: string;
}

interface Props {
  value: string[];
  onChange: (codenames: string[]) => void;
  options: TagSourceOption[];
  label?: string;
  disabled?: boolean;
  helperText?: string;
}

export function TagSourcesSelect({
  value,
  onChange,
  options,
  label = 'Tag sources',
  disabled,
  helperText,
}: Props) {
  const names = new Map(options.map((o) => [o.codename, o.name]));
  const empty = value.length === 0;
  return (
    <TextField
      select
      required
      fullWidth
      size="small"
      label={label}
      disabled={disabled}
      value={value}
      onChange={(e) => {
        // For a multiple select MUI hands the value through as string[].
        onChange(e.target.value as unknown as string[]);
      }}
      SelectProps={{
        multiple: true,
        renderValue: (selected) => (selected as string[]).map((c) => names.get(c) ?? c).join(', '),
      }}
      error={empty}
      helperText={empty ? 'Pick at least one source.' : helperText}
    >
      {options.map((o) => (
        <MenuItem key={o.codename} value={o.codename} dense>
          <Checkbox size="small" checked={value.includes(o.codename)} sx={{ py: 0, mr: 1 }} />
          <ListItemText primary={o.name} secondary={o.codename} />
        </MenuItem>
      ))}
    </TextField>
  );
}
